import { useState } from 'react'
import { api } from '../lib/api'

export default function LiveQuote() {
  const [input, setInput]       = useState('')
  const [symbol, setSymbol]     = useState('')
  const [quote, setQuote]       = useState<any>(null)
  const [isLoading, setLoading] = useState(false)
  const [error, setError]       = useState<string | null>(null)

  async function lookup(sym: string) {
    if (!sym) return
    const upper = sym.toUpperCase()
    setSymbol(upper)
    setInput('')
    setError(null)
    setLoading(true)
    setQuote(null)
    try {
      const result = await api.market.quote(upper)
      setQuote(result)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const changePos   = quote && typeof quote.change === 'number' ? quote.change >= 0 : null
  const changeColor = changePos === true ? 'var(--cash)' : changePos === false ? 'var(--red)' : 'var(--muted)'

  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: '16px 18px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div className="eyebrow">Live Market Data</div>
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.54rem', color: 'var(--cash)', padding: '2px 7px', border: '1px solid var(--cash-dim)', background: 'var(--stamp)', borderRadius: 2 }}>
          Finnhub
        </div>
        {quote?.source && quote.source !== 'Finnhub' && (
          <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--amber)', padding: '2px 6px', border: '1px solid rgba(212,168,67,.3)', borderRadius: 2 }}>
            via {quote.source}
          </div>
        )}
        {quote?.warning && (
          <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--amber)', marginLeft: 'auto' }}>
            {quote.warning}
          </div>
        )}
      </div>

      {/* Search */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value.toUpperCase())}
          onKeyDown={e => e.key === 'Enter' && lookup(input)}
          placeholder="Enter ticker…"
          maxLength={10}
          style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '7px 10px', color: 'var(--text)', fontFamily: 'Syne Mono', fontSize: '.75rem', outline: 'none', flex: 1 }}
        />
        <button
          onClick={() => lookup(input)}
          disabled={!input || isLoading}
          style={{ padding: '7px 14px', background: input && !isLoading ? 'var(--cash)' : 'var(--paper)', color: input && !isLoading ? 'var(--ink)' : 'var(--muted)', border: '1px solid var(--edge)', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '.72rem', cursor: input ? 'pointer' : 'default', transition: 'all .15s' }}>
          {isLoading ? '...' : 'Look up'}
        </button>
      </div>

      {/* Quick picks */}
      <div style={{ display: 'flex', gap: 5, marginBottom: 14, flexWrap: 'wrap' }}>
        {['SPY','QQQ','AAPL','NVDA','TSLA','AMZN','BTC'].map(s => (
          <button key={s} onClick={() => lookup(s)} style={{
            padding: '3px 8px',
            background: symbol === s && quote ? 'var(--stamp)' : 'transparent',
            border: `1px solid ${symbol === s && quote ? 'var(--cash-dim)' : 'var(--edge)'}`,
            color: symbol === s && quote ? 'var(--cash)' : 'var(--muted)',
            fontFamily: 'Syne Mono', fontSize: '.58rem', borderRadius: 2, cursor: 'pointer',
          }}>{s}</button>
        ))}
      </div>

      {/* Idle */}
      {!quote && !isLoading && !error && (
        <div style={{ padding: '20px 0', textAlign: 'center', opacity: .3, fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)' }}>
          Enter a ticker or pick one above
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)', padding: '10px 0' }}>
          Fetching {symbol}...
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--red)', background: 'rgba(224,92,92,.06)', border: '1px solid rgba(224,92,92,.2)', borderRadius: 2, padding: '8px 10px', lineHeight: 1.6 }}>
          ✗ {error}
        </div>
      )}

      {/* Quote */}
      {quote && !error && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.5rem', color: 'var(--bill)' }}>{quote.symbol}</div>
            <div style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.8rem', color: changeColor }}>
              ${typeof quote.price === 'number' ? quote.price.toFixed(2) : '—'}
            </div>
            <div style={{ fontFamily: 'Syne Mono', fontSize: '.7rem', color: changeColor }}>
              {quote.change >= 0 ? '+' : ''}{typeof quote.change === 'number' ? quote.change.toFixed(2) : '—'} ({quote.changePct})
            </div>
            {quote.cached && (
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)', marginLeft: 'auto', display:'flex', alignItems:'center', gap:3 }}><img src="/icons/dca_clock.svg" alt="" style={{width:9,height:9,opacity:.5}}/> cached</div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
            {[
              { label: 'Open',   val: `$${quote.open?.toFixed(2) ?? '—'}` },
              { label: 'High',   val: `$${quote.high?.toFixed(2) ?? '—'}` },
              { label: 'Low',    val: `$${quote.low?.toFixed(2) ?? '—'}` },
              { label: 'Volume', val: quote.volume?.toLocaleString() ?? '—' },
            ].map(m => (
              <div key={m.label} style={{ background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2, padding: '7px 9px' }}>
                <div className="eyebrow" style={{ marginBottom: 3 }}>{m.label}</div>
                <div style={{ fontFamily: 'Syne Mono', fontSize: '.68rem', color: 'var(--text)' }}>{m.val}</div>
              </div>
            ))}
          </div>

          <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', color: 'var(--muted)', opacity: .5 }}>
            As of {quote.latestDay} · {quote.cached ? 'cached · no call used' : `via ${quote.source}`}
          </div>
        </div>
      )}
    </div>
  )
}
